/**
 * SAM 3 prompt bench — shelves only.
 *
 * Shelf fullness is pre-labelled by SAM 3, and every prompt in that set was chosen by
 * trying it and looking at the masks. That work used to happen in a container on a
 * laptop, which meant only one person could do it and only away from the crops. This
 * puts it where the crops are: paste a picture (or take the one under the cursor), type
 * a prompt, see what it segments.
 *
 * Collapsed by default — it is a tool for the rare question, not part of labelling, and
 * a labeller working through a queue should never have to look at it.
 *
 * It spends money: one Replicate call per prompt, ~$0.001 each. Hence no live preview,
 * no run-as-you-type — the request goes only when the button is pressed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Play, Clipboard, ImageDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import useSWR from 'swr';
import { getSamDefaults, shelfSamProbe, type SamProbeResult } from '@/api/shelfPool';
import { Tooltip } from '@/components/ui/tooltip';

/** What the bench is pointed at. A paste travels as bytes; a pooled crop travels as an
 *  id, so a 2 MB picture the server can fetch itself is not round-tripped through the
 *  browser. Both carry a `url` because both are shown the moment they are chosen — the
 *  panel should never make you press Run to find out what it is holding. */
type Picked =
  | { kind: 'paste'; url: string }
  | { kind: 'item'; id: number; url: string };

export default function SamPromptBench(
  { item }: { item?: { id: number; crop_url: string } | null },
) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [prompts, setPrompts] = useState('');
  const [threshold, setThreshold] = useState(0.3);
  const [maxFrac, setMaxFrac] = useState(0.03);

  // The production set, fetched once the panel is opened. A labeller should not be
  // guessing prompts: the question is what the REAL pass sees on this crop, and the
  // answer is only that if the same prompts run. Trying an addition alongside them is
  // the point of the box being editable — it is not a blank invitation.
  const { data: defaults } = useSWR(open ? 'sam-defaults' : null,
                                    () => getSamDefaults().then(r => r.data));
  const applyDefaults = useCallback(() => {
    if (!defaults) return;
    setPrompts(defaults.prompts.join(', '));
    setThreshold(defaults.threshold);
    setMaxFrac(defaults.max_instance_frac);
  }, [defaults]);

  /** Point the bench at what production runs, cut and all — the comparison worth having
   *  when a broad prompt gives a surprising number. */
  const applyProduction = useCallback(() => {
    if (!defaults) return;
    setPrompts(defaults.production_prompts.join(', '));
    setThreshold(defaults.threshold);
    setMaxFrac(defaults.production_max_instance_frac);
  }, [defaults]);
  // Prefill once, and never overwrite what someone has typed since.
  const seeded = useRef(false);
  useEffect(() => {
    if (defaults && !seeded.current) { seeded.current = true; applyDefaults(); }
  }, [defaults, applyDefaults]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<SamProbeResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  /** Ctrl+V anywhere while the panel is open. Scoped to `open` so the shortcut cannot
   *  surprise someone who is only labelling. */
  useEffect(() => {
    if (!open) return;
    const onPaste = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.items ?? [])
        .find(i => i.type.startsWith('image/'))?.getAsFile();
      if (!file) return;
      e.preventDefault();
      const fr = new FileReader();
      fr.onload = () => {
        setPicked({ kind: 'paste', url: String(fr.result) });
        setRes(null); setErr(null);
      };
      fr.readAsDataURL(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open]);

  /** Take the crop under the cursor and show it here, so the bench can be used without
   *  screenshotting the page back into itself. */
  const takeFocused = useCallback(() => {
    if (!item) return;
    setPicked({ kind: 'item', id: item.id, url: item.crop_url });
    setRes(null); setErr(null);
  }, [item]);

  const run = useCallback(async () => {
    // Nothing chosen falls back to the crop under the cursor — the common case is
    // "this one", and making that require a click first would be ceremony.
    const target: Picked | null =
      picked ?? (item ? { kind: 'item', id: item.id, url: item.crop_url } : null);
    if (!target) { setErr(t('data.sam.needImage')); return; }
    setBusy(true); setErr(null);
    try {
      const body = target.kind === 'paste'
        ? { image: target.url, prompts, threshold, max_instance_frac: maxFrac }
        : { item_id: target.id, prompts, threshold, max_instance_frac: maxFrac };
      const r = await shelfSamProbe(body);
      setRes(r.data);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(detail || t('data.sam.failed'));
    } finally {
      setBusy(false);
    }
  }, [picked, item, prompts, threshold, maxFrac, t]);

  return (
    <div className="rounded-md border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium"
        onClick={() => setOpen(o => !o)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {t('data.sam.title')}
        <span className="ml-2 text-xs font-normal text-muted-foreground">
          {t('data.sam.subtitle')}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t px-3 py-3 text-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="min-w-64 flex-1">
              <span className="mb-1 block text-xs text-muted-foreground">
                {t('data.sam.prompts')}
              </span>
              <input
                className="w-full rounded-md border bg-background px-2 py-1.5"
                value={prompts}
                onChange={e => setPrompts(e.target.value)}
                placeholder={(defaults?.prompts ?? []).join(', ')}
              />
            </label>
            <label>
              <span className="mb-1 block text-xs text-muted-foreground">
                {t('data.sam.threshold')}
              </span>
              <input
                type="number" step="0.05" min="0.01" max="0.99"
                className="w-24 rounded-md border bg-background px-2 py-1.5"
                value={threshold}
                onChange={e => setThreshold(Number(e.target.value))}
              />
            </label>
            <label>
              <Tooltip content={t('data.sam.maxFracHint')}><span className="mb-1 block text-xs text-muted-foreground">
                {t('data.sam.maxFrac')}
              </span></Tooltip>
              <input
                type="number" step="0.01" min="0" max="1"
                className="w-24 rounded-md border bg-background px-2 py-1.5"
                value={maxFrac}
                onChange={e => setMaxFrac(Number(e.target.value))}
              />
            </label>
            <Button size="sm" onClick={run} disabled={busy}>
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    : <Play className="mr-1.5 h-4 w-4" />}
              {t('data.sam.run')}
            </Button>
            <Button size="sm" variant="ghost" onClick={applyDefaults} disabled={!defaults}
                    title={t('data.sam.resetHint')}>
              {t('data.sam.reset')}
            </Button>
            <Button size="sm" variant="ghost" onClick={applyProduction} disabled={!defaults}
                    title={t('data.sam.productionHint')}>
              {t('data.sam.production')}
            </Button>
            <Button size="sm" variant="outline" onClick={takeFocused} disabled={!item}>
              <ImageDown className="mr-1.5 h-4 w-4" />
              {t('data.sam.takeFocused')}
            </Button>
            {picked && (
              <Button size="sm" variant="ghost" onClick={() => { setPicked(null); setRes(null); }}>
                {t('data.sam.clearImage')}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clipboard className="h-3.5 w-3.5" />
            {picked
              ? (picked.kind === 'paste' ? t('data.sam.pasted') : t('data.sam.takenFocused'))
              : item ? t('data.sam.willUseFocused') : t('data.sam.pasteHint')}
          </div>

          {/* Whatever was chosen is on screen straight away. Until Run is pressed this is
              the only picture; afterwards the two result panes replace it, because by
              then the original comes back from the server at the size the model saw. */}
          {picked && !res && (
            <figure className="max-w-md space-y-1">
              <img src={picked.url} alt="" className="w-full rounded border" />
              <figcaption className="text-xs text-muted-foreground">
                {t('data.sam.preview')}
              </figcaption>
            </figure>
          )}

          {err && <div className="text-destructive">{err}</div>}
          {res?.per_prompt.some(p => p.error === 'throttled') && (
            <div className="text-xs text-amber-700">{t('data.sam.throttledHint')}</div>
          )}

          {res && (
            <>
              {/* The headline number, because it is the question people actually have:
                  what would SAM call this shelf? Shown with its arithmetic — painted
                  pixels over total — so it reads as a measurement rather than an
                  opinion. On a pooled crop it IS the label; on a pasted picture it is
                  coverage of whatever was pasted, and the caption says which. */}
              <div className="rounded-md border bg-muted/40 px-3 py-2">
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="text-2xl font-semibold tabular-nums">{res.coverage}%</span>
                  <span className="text-sm">
                    {picked?.kind === 'paste'
                      ? t('data.sam.coveragePasted') : t('data.sam.coverageShelf')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t('data.sam.unionOf').replace('{n}', String(res.per_prompt.length))}
                  </span>
                </div>
                {/* Said out loud, because the whole point of a bench is that you can
                    trust what it tells you — including about its own limits. */}
                <div className="mt-1 text-xs text-muted-foreground">
                  {picked?.kind !== 'paste' && t('data.sam.notProduction')}
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {res.union_px.toLocaleString()} / {res.total_px.toLocaleString()}{' '}
                    {t('data.sam.pixels')}
                  </span>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {res.original && (
                  <figure className="space-y-1">
                    <img src={res.original} alt="" className="w-full rounded border" />
                    <figcaption className="text-xs text-muted-foreground">
                      {t('data.sam.paneOriginal')} · {res.width}×{res.height}
                    </figcaption>
                  </figure>
                )}
                {res.overlay && (
                  <figure className="space-y-1">
                    <img src={res.overlay} alt="" className="w-full rounded border" />
                    <figcaption className="text-xs text-muted-foreground">
                      {t('data.sam.paneMasked')}
                    </figcaption>
                  </figure>
                )}
              </div>

              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th className="py-1">{t('data.sam.colPrompt')}</th>
                    <th>{t('data.sam.colFound')}</th>
                    <th>{t('data.sam.colKept')}</th>
                    <th>{t('data.sam.colDropped')}</th>
                    <th>{t('data.sam.colCoverage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {res.per_prompt.map(p => (
                    <tr key={p.prompt} className="border-t">
                      <td className="py-1 font-medium">{p.prompt}</td>
                      <td>{p.instances}</td>
                      <td>{p.kept}</td>
                      {/* Dropped is the number that matters: instances bigger than the
                          size cut are a tray or the case, never one product. */}
                      <td className={p.dropped ? 'text-amber-700' : ''}>{p.dropped}</td>
                      {/* A throttle is not a failure of the prompt, and showing it as
                          one sends people rewriting prompts that were fine. */}
                      <td>{p.error
                        ? <Tooltip content={p.error}><span className={p.error === 'throttled'
                              ? 'text-amber-700' : 'text-destructive'}>
                            {p.error === 'throttled' ? t('data.sam.throttled') : '—'}
                          </span></Tooltip>
                        : `${p.coverage}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Said plainly because the table invites the wrong arithmetic: two prompts
                  can find the same bottle, so the column does not add up to the headline. */}
              <div className="text-xs text-muted-foreground">
                {t('data.sam.overlapNote')} · {t('data.sam.calls')}: {res.calls}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
